// src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// #region agent log
import { dbg } from './utils/debugLog';
// #endregion

// Route-level code splitting: each page loads as its own chunk on demand.
// This keeps the initial bundle small (the three.js landing scene, recharts,
// icon packs, etc. only download for the pages that use them) and makes
// in-app navigation snappy.
const HomeLander           = lazy(() => import('./pages/HomeLander'));
const TripperProfile       = lazy(() => import('./pages/tripper/TripperProfile'));
const TripperQuests        = lazy(() => import('./pages/tripper/TripperQuests'));
const TripperConnections   = lazy(() => import('./pages/tripper/TripperConnections'));
const UserProfile          = lazy(() => import('./pages/tripper/UserProfile'));
const BusinessProfile      = lazy(() => import('./pages/partner/BusinessProfile'));
const BusinessQuest        = lazy(() => import('./pages/partner/BusinessQuest'));
const PartnerPublicProfile = lazy(() => import('./pages/tripper/PartnerPublicProfile'));
const DiscoverPartners     = lazy(() => import('./pages/tripper/DiscoverPartners'));
const MyBookings           = lazy(() => import('./pages/tripper/MyBookings'));
const Messages             = lazy(() => import('./pages/messages/Messages'));
const ResetPassword        = lazy(() => import('./pages/ResetPassword'));
const AdminDashboard       = lazy(() => import('./pages/admin/AdminDashboard'));
const NotFound             = lazy(() => import('./pages/NotFound'));
const AppShell             = lazy(() => import('./layouts/AppShell'));

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Minimal branded fallback shown only while a route chunk downloads.
const RouteFallback = () => {
  // #region agent log
  useEffect(() => {
    dbg('App.js:RouteFallback', 'route-fallback-shown', { path: window.location.pathname }, 'B');
    return () => dbg('App.js:RouteFallback', 'route-fallback-hidden', { path: window.location.pathname }, 'B');
  }, []);
  // #endregion
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0a0a0c' }}>
      <div style={{
        width: 42, height: 42, borderRadius: '50%',
        border: '3px solid rgba(236,72,153,0.75)', borderTopColor: 'transparent',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// #region agent log
// Logs every client-side route change so we can time nav-click → page-swap.
const RouteChangeLogger = () => {
  const location = useLocation();
  useEffect(() => {
    dbg('App.js:RouteChangeLogger', 'route-change', { path: location.pathname }, 'B');
  }, [location.pathname]);
  return null;
};
// #endregion

function App() {
  const { i18n } = useTranslation();

  // 1) Prime CSRF cookie. Swallow network errors so an unreachable API
  // (e.g. during local landing-page dev) doesn't surface as an unhandled
  // promise rejection in the console.
  useEffect(() => {
    fetch(`${API_BASE}/api/auth/csrf-token`, {
      credentials: 'include'
    }).catch(() => {});
  }, []);

  // #region agent log
  useEffect(() => {
    let navType = 'unknown', bootCount = 0, heartbeatGapMs = null;
    try {
      const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      navType = (nav && nav.type) || 'unknown';
      bootCount = (parseInt(sessionStorage.getItem('dbg-boots') || '0', 10) || 0) + 1;
      sessionStorage.setItem('dbg-boots', String(bootCount));
      const lastBeat = parseInt(sessionStorage.getItem('dbg-beat') || '0', 10);
      if (lastBeat) heartbeatGapMs = Date.now() - lastBeat;
    } catch (e) { /* noop */ }
    dbg('App.js:boot', 'app-boot', {
      build: 'dbg-0712b',
      navType, bootCount, heartbeatGapMs,
      mem: (performance.memory && Math.round(performance.memory.usedJSHeapSize / 1048576)) || null,
      sw: !!(navigator.serviceWorker && navigator.serviceWorker.controller),
      vw: window.innerWidth, vh: window.innerHeight,
      ua: navigator.userAgent.slice(0, 90),
    }, 'F');
    // Heartbeat every 2s: lets us measure how long the tab was dead before a
    // reboot (small gap + type 'reload' = browser-initiated crash reload).
    const beat = setInterval(() => {
      try { sessionStorage.setItem('dbg-beat', String(Date.now())); } catch (e) { /* noop */ }
    }, 2000);
    // Main-thread stall watchdog: a 500ms timer that measures its own drift.
    // One huge stall = synchronous render block; many repeated stalls =
    // continuous animation/paint saturation. Only visible-tab stalls count.
    let lastTick = Date.now();
    const stallTimer = setInterval(() => {
      const now = Date.now();
      const drift = now - lastTick - 500;
      lastTick = now;
      if (drift > 400 && document.visibilityState === 'visible') {
        dbg('App.js:stall', 'main-thread-stall', { ms: Math.round(drift), path: window.location.pathname }, 'G');
      }
    }, 500);
    const onErr = (e) => dbg('App.js:onerror', 'window-error', {
      msg: String((e && (e.message || e.error)) || '').slice(0, 300),
      src: (e && e.filename) || '',
    }, 'A');
    const onRej = (e) => dbg('App.js:onrejection', 'unhandled-rejection', {
      name: e && e.reason && e.reason.name,
      msg: String((e && e.reason && (e.reason.message || e.reason)) || '').slice(0, 300),
    }, 'A');
    const onCtxLost = (e) => dbg('App.js:webgl', 'webgl-context-lost', {
      tag: e.target && e.target.tagName, path: window.location.pathname,
    }, 'F');
    const onVis = () => dbg('App.js:visibility', 'visibility-change', {
      state: document.visibilityState, path: window.location.pathname,
    }, 'F');
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onRej);
    window.addEventListener('webglcontextlost', onCtxLost, true);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(beat);
      clearInterval(stallTimer);
      window.removeEventListener('error', onErr);
      window.removeEventListener('unhandledrejection', onRej);
      window.removeEventListener('webglcontextlost', onCtxLost, true);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
  // #endregion

  // 2) RTL support for Arabic
  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // 2b) Recover from stale-chunk errors. Sessions opened before a deploy
  // reference lazy chunks that may no longer exist; without this, the app
  // crashes to a blank screen. Reload once (rate-limited) to pick up the
  // fresh bundle.
  useEffect(() => {
    const onChunkError = (e) => {
      const reason = e && (e.reason || e.error);
      const msg = String((reason && reason.message) || e.message || '');
      const isChunkError = (reason && reason.name === 'ChunkLoadError') || /Loading chunk .+ failed/i.test(msg);
      if (!isChunkError) return;
      const KEY = 'wesafar-chunk-reload';
      let last = 0;
      try { last = parseInt(sessionStorage.getItem(KEY) || '0', 10) || 0; } catch (err) { /* noop */ }
      if (Date.now() - last > 60000) {
        try { sessionStorage.setItem(KEY, String(Date.now())); } catch (err) { /* noop */ }
        window.location.reload();
      }
    };
    window.addEventListener('error', onChunkError);
    window.addEventListener('unhandledrejection', onChunkError);
    return () => {
      window.removeEventListener('error', onChunkError);
      window.removeEventListener('unhandledrejection', onChunkError);
    };
  }, []);

  const user = useSelector(state => state.auth.user);

  // 3) Once logged in, prefetch the user's likely destinations during idle
  // time so nav clicks resolve instantly (webpack dedupes these imports).
  useEffect(() => {
    if (!user) return;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    idle(() => {
      if (user.isTripper) {
        import('./pages/tripper/TripperProfile');
        import('./pages/tripper/TripperQuests');
        import('./pages/tripper/TripperConnections');
        import('./pages/tripper/DiscoverPartners');
        import('./pages/tripper/MyBookings');
      } else {
        import('./pages/partner/BusinessProfile');
        import('./pages/partner/BusinessQuest');
      }
      import('./pages/messages/Messages');
    });
  }, [user]);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {/* #region agent log */}
      <RouteChangeLogger />
      {/* #endregion */}
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<HomeLander />} />

        {/* Authenticated app: the shell keeps the navbar mounted across
            route changes so tab taps only swap the page content. */}
        <Route element={user ? <AppShell /> : <Navigate to="/" replace />}>
          <Route path="/tripper/profile" element={<TripperProfile />} />
          <Route path="/tripper/quests" element={<TripperQuests />} />
          <Route path="/tripper/connections" element={<TripperConnections />} />
          <Route path="/tripper/user/:userName" element={<UserProfile />} />

          <Route path="/partner/profile" element={<BusinessProfile />} />
          <Route path="/partner/quest" element={<BusinessQuest />} />
          <Route path="/partner/public/:id" element={<PartnerPublicProfile />} />
          <Route path="/tripper/discover" element={<DiscoverPartners />} />
          <Route path="/tripper/bookings" element={<MyBookings />} />

          {/* Messaging - available to both trippers and partners */}
          <Route path="/messages" element={<Messages />} />
        </Route>

        {/* Legacy path: older cached bundles navigated here after logout,
            hitting the 404 catch-all. Redirect home instead. */}
        <Route path="/login" element={<Navigate to="/" replace />} />

        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Admin dashboard - requires auth + isAdmin */}
        <Route
          path="/admin"
          element={user?.isAdmin ? <AdminDashboard /> : <Navigate to="/" replace />}
        />

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;